import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Copy } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { Button } from "~/components/ui";
import { useComposerList } from "~/features/compoundRecordComposers/hooks";
import type { ComposerRow, CompoundRecordComposerSearchCondition } from "~/features/compoundRecordComposers/types";
import { toComposerRowList } from "~/features/compoundRecordComposers/utils/transformers";

interface CompoundRecordComposersTableProps {
  searchConditions: CompoundRecordComposerSearchCondition[];
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onSelect: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, ComposerRow>();

export function CompoundRecordComposersTable({
  searchConditions,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onSelect,
}: CompoundRecordComposersTableProps) {
  const { t } = useTranslation();
  const { list, count } = useComposerList({ searchConditions, pageIndex, pageSize });

  const rows = useMemo(() => toComposerRowList(list.data.compoundRecordComposers), [list.data.compoundRecordComposers]);
  const totalCount = count.data.compoundRecordComposersAggregate.aggregate?.count ?? 0;

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, ComposerRow, any>[] = [
    helper.accessor("id", {
      id: "id",
      header: () => t("model.common.id"),
      enableSorting: false,
      cell: (info) => {
        const id = info.getValue();
        return (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-12-regular text-fg-2">{id}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 text-fg-3"
              aria-label={t("ui.copyId")}
              onClick={() => handleCopyId(id)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        );
      },
    }),
    helper.accessor("key", {
      id: "key",
      header: () => t("model.common.key"),
      cell: (info) => <span className="font-mono text-12-regular text-fg-1">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("recordType", {
      id: "recordType",
      header: () => t("model.common.recordType"),
      enableSorting: false,
      cell: (info) => <span className="text-14-regular text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("updatedAt", {
      id: "updatedAt",
      header: () => t("model.common.updatedAt"),
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => onSelect(info.row.original.id)}>
            {t("ui.view")}
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <RemoteDataTable<ComposerRow>
      columns={columns}
      data={rows}
      rowCount={totalCount}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      sorting={[]}
      onSortingChange={() => undefined}
      emptyMessage={t("feature.compoundRecordComposers.empty")}
    />
  );
}
